import { debounce } from "lodash-es";
import { useEffect } from "react";
import type { FieldAnchor } from "web_myplann/Shared/Types/FieldAnchor";
import { createFieldAnchor } from "web_myplann/Shared/Utils/createFieldAnchor";
import { getTextFieldForTarget } from "web_myplann/Shared/Utils/getTextFieldForTarget";
import { holdFieldAtAnchor } from "web_myplann/Shared/Utils/holdFieldAtAnchor";
import { revealFocusedField } from "web_myplann/Shared/Utils/revealFocusedField";

const isIosWebView =
	!!window.ReactNativeWebView && !/android/i.test(navigator.userAgent);

const FIELD_REVEAL_DELAY_MS = 100;

export const useKeyboardScrollGuard = () => {
	useEffect(() => {
		const viewport = window.visualViewport;
		if (!isIosWebView || !viewport) {
			return;
		}

		let anchor: FieldAnchor | null = null;
		let lastViewportHeight = viewport.height;

		const clampScroll = () => {
			const scrolled = document.scrollingElement?.scrollTop ?? window.scrollY;
			if (scrolled < 0 || window.scrollY < 0 || viewport.offsetTop < 0) {
				window.scrollTo({ top: 0, left: 0, behavior: "instant" });
			}
		};

		const holdField = () => {
			if (anchor) {
				holdFieldAtAnchor(anchor);
			}
		};

		const releaseField = () => {
			anchor = null;
		};

		const settleField = debounce(() => {
			releaseField();
			revealFocusedField(viewport.height);
		}, FIELD_REVEAL_DELAY_MS);

		const handleViewportChange = () => {
			clampScroll();
			holdField();
			requestAnimationFrame(clampScroll);
		};

		const handleViewportResize = () => {
			if (viewport.height < lastViewportHeight) {
				anchor = createFieldAnchor(viewport.height);
				settleField();
			}
			lastViewportHeight = viewport.height;
			handleViewportChange();
		};

		const handleWindowResize = () => {
			holdField();
			settleField();
		};

		const handleMouseDown = (event: MouseEvent) => {
			const field = getTextFieldForTarget(event.target);
			if (!field || field === document.activeElement) {
				return;
			}

			event.preventDefault();
			field.focus({ preventScroll: true });
			field.setSelectionRange(field.value.length, field.value.length);
		};

		document.addEventListener("mousedown", handleMouseDown, true);
		viewport.addEventListener("resize", handleViewportResize);
		viewport.addEventListener("scroll", handleViewportChange);
		window.addEventListener("resize", handleWindowResize);
		window.addEventListener("touchstart", releaseField, { passive: true });
		window.addEventListener("focusout", releaseField);

		return () => {
			document.removeEventListener("mousedown", handleMouseDown, true);
			viewport.removeEventListener("resize", handleViewportResize);
			viewport.removeEventListener("scroll", handleViewportChange);
			window.removeEventListener("resize", handleWindowResize);
			window.removeEventListener("touchstart", releaseField);
			window.removeEventListener("focusout", releaseField);
			settleField.cancel();
		};
	}, []);
};
